import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FaRegCircleCheck } from "react-icons/fa6";
import MainButton from "../MainButton";
import useFetchPrice from "../../hooks/useFetchPrice";
import useSubscription from "../../hooks/useSubscription";

interface SubscriptionCardProps {
  planKey: string;
  planType: string;
  productId: string;
  features: string[];
  description: string;
  isMonthly: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  planKey,
  planType,
  productId,
  features,
  description,
  isMonthly,
}) => {
  const navigate = useNavigate();
  const {
    price,
    loading: priceLoading,
    error: priceError,
  } = useFetchPrice(productId);
  const {
    handleSubscriptionAction,
    loading: subscriptionLoading,
    error: subscriptionError,
    isSubscribedStripe,
    user,
  } = useSubscription(productId);
  const [open, setOpen] = useState(false);

  const handleButtonClick = () => {
    if (!user) {
      setOpen(true); // Open the Dialog if not logged in
    } else {
      handleSubscriptionAction();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div
      className={`border-2 rounded-xl max-w-[25rem] ${
        planKey === "premium"
          ? "border-primary border-8"
          : "border-txtSecondary/20 border-2 scale-95"
      } p-8 w-full bg-white shadow-lg flex flex-col justify-between`}
    >
      <div>
        <div className="flex items-center justify-between">
          <Typography variant="h6" className="font-bold capitalize">
            {planKey}
          </Typography>

          <Box
            className={`bg-primary/10 text-primary text-sm font-bold rounded-full py-2 px-4 ${
              isMonthly ? "invisible" : "visible"
            }`}
          >
            Save 3 months
          </Box>
        </div>

        <Typography variant="h4" className="font-extrabold">
          {priceLoading ? "Loading..." : priceError ? "Error" : `$${price}`}
          <span className="text-txtSecondary">
            /{isMonthly ? "month" : "year"}
          </span>
        </Typography>
        <Typography>{description}</Typography>

        <Box className="flex-col gap-2 mt-8">
          {features.map((feature, index) => (
            <Box key={index} className="flex items-center gap-2">
              <FaRegCircleCheck className="text-[#1a9146]" />
              <Typography>{feature}</Typography>
            </Box>
          ))}
        </Box>
      </div>
      <br />
      <MainButton
        text={isSubscribedStripe ? "Manage Subscription" : "Subscribe"}
        onClick={handleButtonClick}
        disabled={subscriptionLoading || priceLoading}
        loading={subscriptionLoading}
        isSecondary={planKey === "standard"}
        margin="mt-10"
      />
      {subscriptionError && (
        <Typography className="text-red-500">{subscriptionError}</Typography>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"Login Required"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You need to log in to subscribe. Please log in to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={() => navigate("/auth")} color="primary" autoFocus>
            Login
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SubscriptionCard;
